import { z } from "zod";

// §9.15 / D54 — Promo-Code-System: Effekt-Publish-Gate (P1-3) + Eingabe-Schemas.
// Ein Code = Bündel aus Effekten (effects jsonb), je `trigger`. Dieses Schema validiert
// die effects[] beim Anlegen/Ändern (analog Price-Book-Publish-Gate §9.7) — stabile id,
// gültige trigger/type-Kombi, genau eines von credits|percent, percent ≤ Obergrenze,
// validity_days > 0. Effekte werden NUR validiert-persistiert; die DB-Engine (0013) liest
// sie als vertrauten Input.

/** Obergrenze für Rabatt-/Bonus-Prozente (§13: Schwelle tunebar). */
export const PROMO_PERCENT_CAP = 100;

/** Reservierter Audit-Markerschlüssel der Engine — nie als echter Effekt erlaubt. */
export const PROMO_EVENT_MARKER = "__event__";

export const PromoTrigger = z.enum(["redeem", "purchase"]);
export type PromoTrigger = z.infer<typeof PromoTrigger>;

export const PromoEffectType = z.enum(["grant_credits", "discount", "bonus_credits"]);
export type PromoEffectType = z.infer<typeof PromoEffectType>;

const EffectId = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, "effect.id: nur [A-Za-z0-9_-]")
  .refine((id) => id !== PROMO_EVENT_MARKER, `'${PROMO_EVENT_MARKER}' ist reserviert`);

const Percent = z.number().positive().max(PROMO_PERCENT_CAP);
const ValidityDays = z.number().int().positive(); // > 0 (§9.15)

// redeem → grant_credits: Instant-Bonus-Lot (1×/User via grant_claimed).
const GrantCreditsEffect = z.object({
  id: EffectId,
  trigger: z.literal("redeem"),
  type: z.literal("grant_credits"),
  credits: z.number().int().positive(),
  validity_days: ValidityDays.optional(),
});

// purchase → discount: Paddle One-Time-Coupon am expliziten Checkout (Coupon-ID am Code).
const DiscountEffect = z.object({
  id: EffectId,
  trigger: z.literal("purchase"),
  type: z.literal("discount"),
  percent: Percent,
  applies_to: z.string().optional(),
});

// purchase → bonus_credits: Bonus-Lot im Kauf-Webhook (credits ODER percent — XOR via gate).
const BonusCreditsEffect = z.object({
  id: EffectId,
  trigger: z.literal("purchase"),
  type: z.literal("bonus_credits"),
  credits: z.number().int().positive().optional(),
  percent: Percent.optional(),
  validity_days: ValidityDays.optional(),
  applies_to: z.string().optional(),
});

export const PromoEffect = z.discriminatedUnion("type", [
  GrantCreditsEffect,
  DiscountEffect,
  BonusCreditsEffect,
]);
export type PromoEffect = z.infer<typeof PromoEffect>;

/** effects[] mit Publish-Regeln: ≥1 Effekt, eindeutige ids, bonus_credits XOR credits|percent. */
export const PromoEffects = z
  .array(PromoEffect)
  .min(1, "mindestens ein Effekt")
  .superRefine((effects, ctx) => {
    const seen = new Set<string>();
    effects.forEach((e, i) => {
      if (seen.has(e.id))
        ctx.addIssue({ code: "custom", path: [i, "id"], message: `doppelte effect.id '${e.id}'` });
      seen.add(e.id);
      if (e.type === "bonus_credits") {
        const hasCredits = e.credits != null;
        const hasPercent = e.percent != null;
        if (hasCredits === hasPercent)
          ctx.addIssue({ code: "custom", path: [i], message: "bonus_credits: genau eines von credits|percent" });
      }
    });
  });
export type PromoEffects = z.infer<typeof PromoEffects>;

/**
 * Voll-Publish-Gate für einen anzulegenden/ändernden Code: normalisiert `code`
 * (UPPER/trim) und erzwingt `paddle_discount_id`, sobald ein discount-Effekt existiert.
 */
export const PromoCodePublish = z
  .object({
    code: z
      .string()
      .trim()
      .min(1)
      .max(64)
      .regex(/^[A-Za-z0-9_-]+$/, "code: nur [A-Za-z0-9_-]")
      .toUpperCase(),
    label: z.string().max(200).optional(),
    effects: PromoEffects,
    paddle_discount_id: z.string().nullable().optional(),
    active: z.boolean().default(true),
    valid_from: z.string().datetime({ offset: true }).nullable().optional(),
    valid_until: z.string().datetime({ offset: true }).nullable().optional(),
    max_users: z.number().int().positive().nullable().optional(),
    per_user_limit: z.number().int().positive().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    const hasDiscount = v.effects.some((e) => e.type === "discount");
    if (hasDiscount && !v.paddle_discount_id)
      ctx.addIssue({
        code: "custom",
        path: ["paddle_discount_id"],
        message: "discount-Effekt erfordert paddle_discount_id (Coupon)",
      });
    // timestamptz-sicher: nach Epoch-ms vergleichen (Offsets ≠ lexikografisch, P2)
    if (v.valid_from && v.valid_until && Date.parse(v.valid_until) <= Date.parse(v.valid_from))
      ctx.addIssue({ code: "custom", path: ["valid_until"], message: "valid_until muss > valid_from sein" });
  });
export type PromoCodePublish = z.infer<typeof PromoCodePublish>;
